import React, { createRef, useRef, useState } from 'react'

import classNames from 'classnames'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { selectIsSuggestRouteFromItinerary } from '../../store/history/history.selectors'
import { SET_STEP_FROM } from '../../store/itinerary/itinerary.constants'
import { setStepIdx } from '../../store/itinerary/itinerarySlice'
import { selectIsItineraryDepartureNow } from '../../store/itineraryOptions/itineraryTimeOptions.selectors'
import { searchItineraryLocation } from '../../store/search/search.actions'
import { suggestServiceCtx } from '../../store/suggest/suggest.service.constants'
import { SPD_RESPONSE_SEARCH_ITINERARY } from '../analytics/BiTagger'
import BackButton from '../header/BackButton'
import SearchInput from '../header/SearchInput'
import { withTranslation } from '../i18n/T'
import { useAction } from '../utils/react-redux'
import BookinSearchExtension from './BookingSearchExtension'
import s from './ItineraryHeaderForm.less'
import { getStepIdx, selectAllSteps } from './steps/steps.selectors'
import StepsButtons from './steps/StepsButtons'

export const ItineraryHeaderForm = ({ t }) => {
  const isItineraryDepartureNow = useSelector(selectIsItineraryDepartureNow)
  const steps = useSelector(selectAllSteps)
  const idx = useSelector(getStepIdx)
  const { getState } = useStore()
  const execSearchItineraryLocation = useAction(searchItineraryLocation)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [value, setValue] = useState('')
  const SearchInputRefs = useRef([])

  const showAllSteps = idx === undefined
  const firstStepToFillIndex = steps.findIndex(({ label }) => !label)
  SearchInputRefs.current = steps.map((_, i) => (SearchInputRefs.current[i] ? SearchInputRefs.current[i] : createRef()))

  const onFocus = idx => () => {
    setValue(steps?.[idx]?.label ?? '')
    dispatch(setStepIdx(idx))
  }

  const onChange = ({ value }) => {
    setValue(value)
  }

  const onSubmit = e => {
    e.preventDefault()
    if (!value || idx >= SearchInputRefs.current.length) return

    setLoading(true)
    BookinSearchExtension.openPopup({ steps, idx, departureNow: isItineraryDepartureNow })

    return SearchInputRefs.current[idx].current
      .onSubmit()
      .then(() =>
        execSearchItineraryLocation({
          terms: value,
          options: { from: SET_STEP_FROM.searchForm },
          tagid: SPD_RESPONSE_SEARCH_ITINERARY
        })
      )
      .catch(e => {
        BookinSearchExtension.closePopup()
      })
      .finally(() => {
        setLoading(false)
        if (selectIsSuggestRouteFromItinerary(getState())) {
          // We’re testing if we’re still on suggest, meaning we’re stuck on an ambiguity
          // It is important to call the selector with getState because we should have changed route after submit()
          BookinSearchExtension.closePopup()
        }
      })
  }

  const getPrefix = i => {
    if (i === 0) return t('itinerary.from')
    if (i === steps.length - 1) return t('itinerary.to')
    return t('itinerary.via')
  }
  const getPlaceholder = i => {
    if (i === 0) return t('itinerary.departure')
    if (i === steps.length - 1) return t('itinerary.arrival')
    return `${t('itinerary.step')} ${i}`
  }

  const renderSteps = () => {
    return steps.map(({ ui: { stepId }, label }, i) => {
      const isFirstStepToFill = i === firstStepToFillIndex
      const showThatStep = idx === i

      return (
        (showAllSteps || showThatStep) && (
          <SearchInput
            key={`${stepId}-${i}-${steps.length}`}
            prefix={getPrefix(i)}
            idx={i}
            ref={SearchInputRefs.current[i]}
            loading={loading}
            placeholder={getPlaceholder(i)}
            suggestFilterType={suggestServiceCtx.filter.places}
            value={showThatStep ? value : label || ''}
            className={classNames(s.default, showThatStep && s.focus, s[`${stepId}`])}
            onFocus={onFocus(i)}
            onChange={onChange}
            isFirstStepToFill={isFirstStepToFill}
          />
        )
      )
    })
  }

  return (
    <>
      <nav>
        <BackButton className={classNames(s.backButton, !showAllSteps && s.focusOnStep)} />
      </nav>
      <form onSubmit={onSubmit} className={classNames(s.ItineraryForm, !showAllSteps && s.focusOnStep)}>
        {renderSteps()}
      </form>
      <div className={s.StepsButtons}>
        <StepsButtons />
      </div>
    </>
  )
}

export default withTranslation(ItineraryHeaderForm)
